// The pixel-art effects either side of a hero title: twenty of them, from
// dividing cells and turning molecules to phages and element tiles, listed in
// CATALOG. Drawn on the left of the title into one low-resolution canvas and
// mirrored onto the right, scaled by a whole number of CSS pixels with
// smoothing off, from a fixed palette, and faded with a 4x4 ordered dither
// rather than with alpha, because partial opacity smears pixel art.
//
// Ported from the design handoff (hero-effects-handoff/hero-effects.js, whose
// README has the spec of each effect) with three changes: it is an ES module
// rather than a UMD script; `pixelSize` may be a function, so the art can
// follow the title's size; the title is measured by its words rather than by
// its element (see `textBox`).
// `hero_effects_controller.js` is its only caller.
//
// The palette is the handoff's and lives here rather than in the tokens: these
// are art colours painted into canvas pixels, not page colours, and no theme
// moves them.

// --- palette ---------------------------------------------------------------
const PALETTE = {
  w: "#f4f6ff", // white: highlights, electrons, hydrogen
  l: "#9db7ff", // light blue: electron trails
  b: "#5b7fd6", // blue: bonds, neutrons
  n: "#3f5fae", // navy: orbit paths
  d: "#34477e", // deep blue: double-bond inner line
  m: "#62e3c3", // mint: cell membrane
  t: "#2a9f92", // teal: cytoplasm dither
  v: "#a77bf0", // violet: nucleus
  p: "#ff8db4", // pink: nucleolus, protons
  r: "#ff6b6b", // red: oxygen
  g: "#8a93ad", // grey: carbon
  y: "#ffe07a", // yellow: sparks, stars
  o: "#ffb35c", // orange: mitochondria, capsules, phosphorus
  k: "#1a2335", // ink: element-tile faces
};
const B4 = [[0, 8, 2, 10], [12, 4, 14, 6], [3, 11, 1, 9], [15, 7, 13, 5]];
function packed(hex) {
  const n = parseInt(hex.slice(1), 16);
  return (0xff000000 | ((n & 255) << 16) | (((n >> 8) & 255) << 8) | (n >> 16)) >>> 0;
}
const C = {};
for (const k in PALETTE) C[k] = packed(PALETTE[k]);

const smooth = (x) => { x = Math.max(0, Math.min(1, x)); return x * x * (3 - 2 * x); };
const clamp01 = (x) => Math.max(0, Math.min(1, x));
const life = (l, max, fin = 0.5, fout = 1) => Math.min(clamp01(l / fin), clamp01((max - l) / fout));
function rng(seed) {
  return function () {
    seed |= 0; seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// --- renderer ----------------------------------------------------------------
// E: { W, H, ex, top, bot, mx }. Pixels with x > ex, y < top or y > bot are
// dropped, so nothing is ever drawn over the title. With mirror on, every
// pixel at x is also written at mx - x.
function createRenderer(buf, E, mirror = true) {
  const g = {
    px(x, y, c, a = 1) {
      x = Math.round(x); y = Math.round(y);
      if (x < 0 || x > E.ex || x >= E.W || y < E.top || y > E.bot || y >= E.H) return;
      if (a < 1 && a * 16 <= B4[y & 3][x & 3]) return; // dithered fade
      buf[y * E.W + x] = c;
      if (mirror) { const xr = E.mx - x; if (xr >= 0 && xr < E.W) buf[y * E.W + xr] = c; }
    },
    line(x0, y0, x1, y1, c, a = 1) {
      x0 = Math.round(x0); y0 = Math.round(y0); x1 = Math.round(x1); y1 = Math.round(y1);
      const dx = Math.abs(x1 - x0), dy = -Math.abs(y1 - y0), sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
      let err = dx + dy;
      for (;;) {
        g.px(x0, y0, c, a);
        if (x0 === x1 && y0 === y1) break;
        const e2 = 2 * err;
        if (e2 >= dy) { err += dy; x0 += sx; }
        if (e2 <= dx) { err += dx; y0 += sy; }
      }
    },
    // rows of palette keys; "." and " " are empty; map overrides keys with packed colours
    sprite(rows, x, y, a = 1, map = null) {
      const ox = Math.round(x) - (rows[0].length >> 1), oy = Math.round(y) - (rows.length >> 1);
      rows.forEach((row, j) => [...row].forEach((ch, i) => { if (ch !== "." && ch !== " ") g.px(ox + i, oy + j, map && map[ch] !== undefined ? map[ch] : C[ch], a); }));
    },
    // Left pixel at xl, right copy at the mirror of xr: text stays readable on both sides.
    pxLR(xl, xr, y, c, a = 1) {
      xl = Math.round(xl); xr = Math.round(xr); y = Math.round(y);
      if (xl < 0 || xl > E.ex || xr < 0 || xr > E.ex || y < E.top || y > E.bot || y >= E.H) return;
      if (a < 1 && a * 16 <= B4[y & 3][xl & 3]) return;
      buf[y * E.W + xl] = c;
      if (mirror) { const r = E.mx - xr; if (r >= 0 && r < E.W) buf[y * E.W + r] = c; }
    },
    spriteU(rows, x, y, a = 1, map = null) {
      const w = rows[0].length, ox = Math.round(x) - (w >> 1), oy = Math.round(y) - (rows.length >> 1);
      rows.forEach((row, j) => [...row].forEach((ch, i) => { if (ch !== "." && ch !== " ") g.pxLR(ox + i, ox + (w - 1 - i), oy + j, map && map[ch] !== undefined ? map[ch] : C[ch], a); }));
    },
    // a four-point star: size 0 a dot, 1 a plus, 2 a plus with longer arms and dim diagonals
    star(x, y, size, arm, a = 1) {
      g.px(x, y, C.w, a);
      for (let d = 1; d <= size; d++) for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) g.px(x + dx * d, y + dy * d, d === 1 && size > 1 ? C.w : arm, a);
      if (size >= 2) for (const [dx, dy] of [[1, 1], [-1, 1], [1, -1], [-1, -1]]) g.px(x + dx, y + dy, arm, a * 0.6);
    },
  };
  return g;
}

// --- the elements ------------------------------------------------------------

// A cell, or a cell mid-division: the outline of a union of discs.
function drawCell(g, discs, a = 1) {
  let x0 = 1e9, y0 = 1e9, x1 = -1e9, y1 = -1e9;
  discs.forEach((d) => { x0 = Math.min(x0, d.x - d.r - 1); y0 = Math.min(y0, d.y - d.r - 1); x1 = Math.max(x1, d.x + d.r + 1); y1 = Math.max(y1, d.y + d.r + 1); });
  const inside = (x, y) => discs.some((d) => (x - d.x) ** 2 + (y - d.y) ** 2 <= d.r * d.r + d.r * 0.6);
  for (let y = Math.floor(y0); y <= y1; y++) for (let x = Math.floor(x0); x <= x1; x++) {
    if (!inside(x, y)) continue;
    const edge = !inside(x + 1, y) || !inside(x - 1, y) || !inside(x, y + 1) || !inside(x, y - 1);
    if (edge) g.px(x, y, C.m, a); else if ((x + y) & 1) g.px(x, y, C.t, a);
  }
  discs.forEach((d) => { g.px(d.x - 1, d.y - 1, C.v, a); g.px(d.x, d.y - 1, C.v, a); g.px(d.x - 1, d.y, C.v, a); g.px(d.x, d.y, C.p, a); });
}

// Molecules as [atoms, bonds]: an atom is [dx, dy, colour], a bond [i, j, double].
const MOLECULES = {
  benzene(th) {
    const at = [], bd = [];
    for (let i = 0; i < 6; i++) { const a = th + (i * Math.PI) / 3; at.push([Math.cos(a) * 6, Math.sin(a) * 6, "g"]); }
    for (let i = 0; i < 6; i++) bd.push([i, (i + 1) % 6, i % 2 === 0]);
    return { at, bd };
  },
  water(th) {
    return { at: [[0, 0, "r"], [Math.cos(th + 0.9) * 5, Math.sin(th + 0.9) * 5, "w"], [Math.cos(th - 0.9 + Math.PI) * 5, Math.sin(th - 0.9 + Math.PI) * 5, "w"]], bd: [[0, 1], [0, 2]] };
  },
  co2(th) {
    return { at: [[0, 0, "g"], [Math.cos(th) * 6, Math.sin(th) * 6, "r"], [-Math.cos(th) * 6, -Math.sin(th) * 6, "r"]], bd: [[0, 1, true], [0, 2, true]] };
  },
  methane(th) {
    const at = [[0, 0, "g"]], bd = [];
    for (let i = 0; i < 4; i++) { const a = th + (i * Math.PI) / 2, rr = i % 2 ? 3.5 : 5.5; at.push([Math.cos(a) * rr, Math.sin(a) * rr, "w"]); bd.push([0, i + 1]); }
    return { at, bd };
  },
};
function drawMolecule(g, type, x, y, th, a = 1) {
  const { at, bd } = MOLECULES[type](th), X = Math.round(x), Y = Math.round(y);
  bd.forEach(([i0, i1, dbl]) => {
    const A = at[i0], B = at[i1];
    g.line(X + A[0], Y + A[1], X + B[0], Y + B[1], C.b, a);
    if (dbl) { const k = 0.6; g.line(X + A[0] * k + (B[0] - A[0]) * 0.2, Y + A[1] * k + (B[1] - A[1]) * 0.2, X + B[0] * k - (B[0] - A[0]) * 0.2, Y + B[1] * k - (B[1] - A[1]) * 0.2, C.d, a); }
  });
  at.forEach(([ax, ay, c]) => {
    const px = Math.round(X + ax), py = Math.round(Y + ay);
    g.px(px, py, C[c], a); g.px(px + 1, py, C[c], a); g.px(px, py + 1, C[c], a); g.px(px + 1, py + 1, C[c === "w" ? "l" : c], a); g.px(px, py, C.w, a);
  });
}

// A Bohr atom: three orbits tilted 60 degrees apart, one electron each.
function drawAtom(g, x, y, th, t, ph, a = 1) {
  const X = Math.round(x), Y = Math.round(y);
  for (let k = 0; k < 3; k++) {
    const phi = th + (k * Math.PI) / 3, c = Math.cos(phi), s = Math.sin(phi), rx = 9, ry = 3.2;
    const pt = (u) => [X + c * rx * Math.cos(u) - s * ry * Math.sin(u), Y + s * rx * Math.cos(u) + c * ry * Math.sin(u)];
    for (let j = 0; j < 44; j += 2) { const [px, py] = pt((j / 44) * 2 * Math.PI); g.px(px, py, C.n, a); }
    const ue = t * 2.6 + k * 2.1 + ph, [ex, ey] = pt(ue), [tx, ty] = pt(ue - 0.25);
    g.px(tx, ty, C.l, a); g.px(ex, ey, C.w, a);
  }
  g.sprite(["pbp", "bpb", "pbp"], X, Y, a);
}

// Shared pieces used by the mixed effects.
function ellipsePt(X, Y, rx, ry, phi, u) { const c = Math.cos(phi), s = Math.sin(phi); return [X + c * rx * Math.cos(u) - s * ry * Math.sin(u), Y + s * rx * Math.cos(u) + c * ry * Math.sin(u)]; }
function atom2(g, x, y, c, a) { x = Math.round(x); y = Math.round(y); g.px(x, y, C.w, a); g.px(x + 1, y, C[c], a); g.px(x, y + 1, C[c], a); g.px(x + 1, y + 1, C[c === "w" ? "l" : c], a); }
function water(g, x, y, u, a, back) { const o = back ? "d" : "r"; atom2(g, x, y, o, a); g.px(x + Math.round(Math.cos(u) * 2.5), y - 1 + Math.round(Math.sin(u)), back ? C.n : C.w, a); g.px(x + 1 - Math.round(Math.cos(u) * 2.5), y - 1 - Math.round(Math.sin(u)), back ? C.n : C.w, a); }
const cellBlob = drawCell;

// --- the effects -------------------------------------------------------------
// Each is (E, R) => (t, dt, g). E.y0..E.y1 is the title's band, E.k scales
// speeds so the motion looks the same at any pixel size.
export const EFFECTS = {
  cells(E, R) {
    const L = []; let acc = 0;
    return (t, dt, g) => {
      acc += dt * 0.55;
      while (acc > 1) { acc--; L.push({ x: E.ex - 3, y: E.y0 + (E.y1 - E.y0) * (0.2 + R() * 0.6), vx: -(5 + R() * 6) * E.k, vy: -(4 + R() * 4) * E.k, r: 4 + ((R() * 2) | 0), l: 0, max: 7 + R() * 2, sd: 0, ang: -Math.PI / 2 + (R() - 0.5) * 1.6, gen: 0 }); }
      for (let i = L.length - 1; i >= 0; i--) {
        const c = L[i]; c.l += dt; c.vx *= Math.pow(0.7, dt); c.x += c.vx * dt; c.y += c.vy * dt;
        if (c.l > c.max || c.y < E.top + c.r) { L.splice(i, 1); continue; }
        if (c.gen === 0 && c.l > 2) c.sd = Math.min(2 * c.r + 1, ((c.l - 2) * (2 * c.r + 1)) / 1.6); // mitosis
        const hx = (Math.cos(c.ang) * c.sd) / 2, hy = (Math.sin(c.ang) * c.sd) / 2;
        const X = Math.round(c.x), Y = Math.round(c.y), a = life(c.l, c.max, 0.6, 1.2);
        if (c.sd >= 2 * c.r + 1) {
          L.splice(i, 1);
          for (const s of [1, -1]) L.push({ x: c.x + hx * s, y: c.y + hy * s, vx: c.vx + Math.cos(c.ang) * s * 3 * E.k - 2 * E.k, vy: c.vy + Math.sin(c.ang) * s * 3 * E.k, r: Math.max(3, c.r - 1), l: c.l, max: c.max, sd: 0, ang: 0, gen: 1 });
          continue;
        }
        drawCell(g, c.sd > 0 ? [{ x: Math.round(X + hx), y: Math.round(Y + hy), r: c.r }, { x: Math.round(X - hx), y: Math.round(Y - hy), r: c.r }] : [{ x: X, y: Y, r: c.r }], a);
      }
    };
  },
  molecules(E, R) {
    const L = [], TYPES = Object.keys(MOLECULES); let acc = 0.8;
    return (t, dt, g) => {
      acc += dt * 0.9;
      while (acc > 1) { acc--; L.push({ x: E.ex - 6, y: E.y0 + (E.y1 - E.y0) * (0.2 + R() * 0.6), vx: -(6 + R() * 9) * E.k, vy: -(4 + R() * 5) * E.k, th: R() * 6, w: (R() < 0.5 ? -1 : 1) * (0.4 + R() * 0.5), ty: TYPES[(R() * 4) | 0], l: 0, max: 7 + R() * 2 }); }
      for (let i = L.length - 1; i >= 0; i--) {
        const m = L[i]; m.l += dt; m.vx *= Math.pow(0.7, dt); m.x += m.vx * dt; m.y += m.vy * dt; m.th += m.w * dt;
        if (m.l > m.max || m.y < E.top + 6) { L.splice(i, 1); continue; }
        drawMolecule(g, m.ty, m.x, m.y, m.th, life(m.l, m.max, 0.6, 1.2));
      }
    };
  },
  atoms(E, R) {
    const L = []; let acc = 0.9;
    return (t, dt, g) => {
      acc += dt * 0.45;
      while (acc > 1 && L.length < 5) { acc--; L.push({ x: E.ex - 9, y: E.y0 + (E.y1 - E.y0) * (0.25 + R() * 0.5), vx: -(5 + R() * 7) * E.k, vy: -(4 + R() * 4) * E.k, th: R() * 3, l: 0, max: 7 + R() * 2, ph: R() * 6 }); }
      if (acc > 1) acc = 1;
      for (let i = L.length - 1; i >= 0; i--) {
        const m = L[i]; m.l += dt; m.vx *= Math.pow(0.7, dt); m.x += m.vx * dt; m.y += m.vy * dt; m.th += 0.25 * dt;
        if (m.l > m.max || m.y < E.top + 9) { L.splice(i, 1); continue; }
        drawAtom(g, m.x, m.y, m.th, t, m.ph, life(m.l, m.max, 0.7, 1.2));
      }
    };
  },

  // --- the other seventeen, exactly as they run on the ideas board ---

  "bubbles"(E, R) {  // 1 · Rising bubbles
  const L=[], pops=[]; let acc=0;
  const S={1:[".#.","#.#",".#."],2:[".##.","#..#","#..#",".##."],3:[".###.","#...#","#...#","#...#",".###."]};
  return (t, dt, g) => {
    acc += dt*2.6;
    while (acc > 1){ acc--; L.push({x:E.ex-R()*3, y:E.y0+R()*(E.y1-E.y0), vx:-(8+R()*18)*E.k, vy:-(6+R()*9)*E.k, r:[1,1,2,2,2,3][R()*6|0], ph:R()*6, l:0, max:3.5+R()*3, c:R()<.5?'l':'m'}); }
    for (let i=L.length-1;i>=0;i--){
      const b=L[i]; b.l+=dt; b.vx*=Math.pow(.55,dt); b.x+=b.vx*dt; b.y+=b.vy*dt;
      const wx = b.x + Math.sin(b.l*3+b.ph)*1.3;
      if (b.l > b.max || b.y < E.top+3){ L.splice(i,1); pops.push({x:wx,y:b.y,l:0}); continue; }
      const a = life(b.l, b.max+1, .4, .01);
      g.sprite(S[b.r], wx, b.y, a, {'#':C[b.c]});
      if (b.r>1) g.px(Math.round(wx)-(b.r===3?1:1), Math.round(b.y)-(b.r===3?1:1), C.w, a);
    }
    for (let i=pops.length-1;i>=0;i--){
      const p=pops[i]; p.l+=dt; if (p.l>.3){pops.splice(i,1);continue;}
      const d=1+p.l*10, a=1-p.l/.3;
      for (const [dx,dy] of [[1,0],[-1,0],[0,1],[0,-1]]) g.px(p.x+dx*d, p.y+dy*d, C.w, a);
      for (const [dx,dy] of [[1,1],[-1,1],[1,-1],[-1,-1]]) g.px(p.x+dx*d*.7, p.y+dy*d*.7, C.l, a*.7);
    }
  };
  },

  "dna"(E, R) {  // 2 · DNA helix
  return (t, dt, g) => {
    const cyc=9, tc=t%cyc, grow=smooth(tc/2.6), fade=tc>7.4?1-(tc-7.4)/1.6:1;
    const Lmax=Math.min(E.ex*0.62, 104), L=Lmax*grow;
    let x=E.ex-1, y=E.y0+(E.y1-E.y0)*.45; const pts=[];
    for (let s=0; s<=L; s+=.5){
      const a=Math.PI+.12+.95*(s/Lmax), nx=-Math.sin(a), ny=Math.cos(a);
      const ph=s*.33-t*2.4, w=4.5*Math.sin(ph);
      const tip=clamp01((L-s)/5)*clamp01(s/3)*(1-.55*smooth((s-.55*Lmax)/(.45*Lmax)))*fade;
      pts.push({s, ax:x+nx*w, ay:y+ny*w, bx:x-nx*w, by:y-ny*w, front:Math.cos(ph)>0, a:tip});
      x+=Math.cos(a)*.5; y+=Math.sin(a)*.5;
    }
    pts.forEach((p,i)=>{ if (i%8===0 && p.s>2){ const mx=(p.ax+p.bx)/2, my=(p.ay+p.by)/2, k=(i/8)%2; g.line(p.ax,p.ay,mx,my,k?C.m:C.v,p.a*.85); g.line(mx,my,p.bx,p.by,k?C.p:C.y,p.a*.85); }});
    pts.forEach(p=>{ g.px(p.front?p.bx:p.ax, p.front?p.by:p.ay, C.n, p.a); });
    pts.forEach(p=>{ g.px(p.front?p.ax:p.bx, p.front?p.ay:p.by, C.l, p.a); });
  };
  },

  "sparkles"(E, R) {  // 3 · Eureka sparkles
  const L=[]; let acc=0;
  return (t, dt, g) => {
    acc+=dt*8;
    while (acc>1){ acc--; const x=E.ex-2-Math.pow(R(),1.8)*E.ex*.7; const yb=E.y0+(E.y1-E.y0)*R(); L.push({x, y:yb-(E.ex-x)*(.25+R()*.45), l:0, max:1+R()*.8, c:['y','l','v','y','m'][R()*5|0]}); }
    for (let i=L.length-1;i>=0;i--){
      const s=L[i]; s.l+=dt; s.y-=5*E.k*dt; s.x-=1.5*E.k*dt;
      if (s.l>s.max){L.splice(i,1);continue;}
      const st=[0,1,2,2,1,0][Math.min(5,(s.l/s.max*6)|0)];
      g.star(Math.round(s.x), Math.round(s.y), st, C[s.c]);
    }
  };
  },

  "spores"(E, R) {  // 6 · Bioluminescent spores
  const L=[];
  const spawn=(l)=>({x:E.ex-1-R()*3, y:E.y0+R()*(E.y1-E.y0), l:l, max:4+R()*3, ph:R()*6, ph2:R()*6, tr:[], acc:0, sp:(7+R()*5)*E.k});
  for (let i=0;i<20;i++) L.push(spawn(-R()*5));
  return (t, dt, g) => {
    L.forEach((s,i)=>{
      s.l+=dt; if (s.l<0) return;
      if (s.l>s.max||s.y<E.top+1||s.x<1){ L[i]=spawn(0); return; }
      const a0=Math.PI+.75+.9*Math.sin(t*.9+s.ph)+.5*Math.sin(t*2.3+s.ph2);
      s.x+=Math.cos(a0)*s.sp*dt; s.y+=Math.sin(a0)*s.sp*dt;
      s.acc+=dt; if (s.acc>.07){ s.acc=0; s.tr.unshift([s.x,s.y]); if (s.tr.length>7) s.tr.pop(); }
      const a=life(s.l,s.max,.5,1);
      s.tr.forEach(([x,y],j)=>g.px(x,y,j<2?C.m:C.t,a*(1-j/7)));
      const glow=Math.sin(t*4+s.ph)>.35;
      g.px(s.x,s.y,glow?C.w:C.m,a);
      if (glow) for (const [dx,dy] of [[1,0],[-1,0],[0,1],[0,-1]]) g.px(s.x+dx,s.y+dy,C.m,a*.55);
    });
  };
  },

  "fireworks"(E, R) {  // 7 · Pipette fireworks
  const D=[], P=[]; let acc=.5;
  return (t, dt, g) => {
    acc+=dt*1.1;
    while (acc>1){ acc--; D.push({x:E.ex-1, y:E.y0+(E.y1-E.y0)*(.1+R()*.8), vx:-(8+R()*18)*E.k, vy:-(36+R()*16)*E.k, c:['m','v','y','l','p'][R()*5|0]}); }
    for (let i=D.length-1;i>=0;i--){
      const d=D[i]; d.vy+=30*E.k*dt; d.x+=d.vx*dt; d.y+=d.vy*dt;
      if (d.vy>-3*E.k || d.y<E.top+4){ D.splice(i,1); const n=10, o=R()*6;
        for (let j=0;j<n;j++){ const a=o+j*2*Math.PI/n, sp=(10+R()*7)*E.k; P.push({x:d.x,y:d.y,vx:Math.cos(a)*sp,vy:Math.sin(a)*sp,l:0,max:.8+R()*.4,c:d.c}); } continue; }
      g.px(d.x,d.y,C.w); g.px(d.x-d.vx*.03,d.y-d.vy*.03,C[d.c]); g.px(d.x-d.vx*.06,d.y-d.vy*.06,C.d);
    }
    for (let i=P.length-1;i>=0;i--){
      const p=P[i]; p.l+=dt; if (p.l>p.max){P.splice(i,1);continue;}
      p.vx*=Math.pow(.2,dt); p.vy=p.vy*Math.pow(.2,dt)+10*E.k*dt; p.x+=p.vx*dt; p.y+=p.vy*dt;
      g.px(p.x,p.y,p.l<.15?C.w:C[p.c],1-p.l/p.max);
    }
  };
  },

  "neurons"(E, R) {  // 8 · Neuron growth
  let seedFor=-1, segs=[], paths=[];
  const SPEED=34;
  function build(seed){
    const r=rng(seed*7919+11); segs=[]; paths=[];
    function br(x,y,a,len,depth,t0,chain){
      a=Math.max(Math.PI+.1,Math.min(Math.PI+1.45,a));  // always outward and up, never down past the subtitle
      const x1=x+Math.cos(a)*len, y1=y+Math.sin(a)*len, t1=t0+len/(SPEED*E.k);
      const s={x0:x,y0:y,x1,y1,t0,t1,depth,len}; segs.push(s); const ch=chain.concat([s]);
      if (depth<4){ const n=r()<.3?1:2; for (let k=0;k<n;k++) br(x1,y1,a+(n===1?(r()-.5)*.5:(k?1:-1)*(.3+r()*.4)),len*(.68+r()*.15),depth+1,t1,ch); }
      else paths.push({chain:ch, off:r()*1.6});
    }
    [[.25,Math.PI+.75],[.75,Math.PI+.35]].forEach(([f,a])=>br(E.ex-1,E.y0+(E.y1-E.y0)*f,a+(r()-.5)*.2,(11+r()*4)*Math.min(1.6,E.k*1.1),0,0,[]));
  }
  return (t, dt, g) => {
    const cyc=10, n=Math.floor(t/cyc), tc=t%cyc; if (n!==seedFor){ seedFor=n; build(n); }
    const fade=tc>8.6?1-(tc-8.6)/1.4:1;
    segs.forEach(s=>{ if (tc<s.t0) return; const p=Math.min(1,(tc-s.t0)/(s.t1-s.t0));
      g.line(s.x0,s.y0,s.x0+(s.x1-s.x0)*p,s.y0+(s.y1-s.y0)*p, s.depth<2?C.b:C.n, fade);
      if (p===1 && s.depth<4){ g.px(s.x1,s.y1,C.l,fade); g.px(s.x1+1,s.y1,C.b,fade); }
      if (p===1 && s.depth===4){ g.px(s.x1,s.y1,C.m,fade); } });
    const grown=Math.max(...segs.map(s=>s.t1));
    if (tc>grown && tc<8.6) paths.forEach(pth=>{
      const total=pth.chain.reduce((a,s)=>a+s.len,0), tt=(tc-grown-pth.off);
      if (tt<0) return; let d=(tt%1.8)*SPEED*1.4*E.k; if (d>total) return;
      for (const s of pth.chain){ if (d<=s.len){ const f=d/s.len; const x=s.x0+(s.x1-s.x0)*f, y=s.y0+(s.y1-s.y0)*f;
        g.px(x,y,C.w); const bf=Math.max(0,f-1.5/s.len); g.px(s.x0+(s.x1-s.x0)*bf,s.y0+(s.y1-s.y0)*bf,C.y); break; } d-=s.len; }
    });
  };
  },

  "icons"(E, R) {  // 10 · Lab icon float
  const SPR=[
    ["..www..","..l.l..","..l.l..",".l...l.","l.....l","lmmmmml","lmmwmml",".lllll."],
    ["www","l.l","l.l","lvl","lvl","lyl",".l."],
    [".ppbb.","pwpbbb","pppbbb",".ppbb."],
    [".lllllll.","l.m..y..l","l..v..m.l",".lllllll."],
    ["b...p",".b.p.","..w..",".p.b.","p...b",".p.b.","..w..",".b.p.","b...p"],
    ["mm...mm","mmm.mmm",".mmtmm.","...t...","...t...","..ggg..","..ggg.."],
  ];
  const L=[]; let acc=.5;
  return (t, dt, g) => {
    acc+=dt*1.3;
    while (acc>1){ acc--; L.push({x:E.ex-5, y:E.y0+(E.y1-E.y0)*(.15+R()*.7), vx:-(10+R()*14)*E.k, vy:-(6+R()*6)*E.k, s:SPR[R()*SPR.length|0], l:0, max:5+R()*2, ph:R()*6}); }
    for (let i=L.length-1;i>=0;i--){
      const m=L[i]; m.l+=dt; m.vx*=Math.pow(.45,dt); m.x+=m.vx*dt; m.y+=m.vy*dt;
      if (m.l>m.max||m.y<E.top+5){L.splice(i,1);continue;}
      const a=life(m.l,m.max,.35,1.2);
      g.sprite(m.s, m.x+Math.sin(m.l*2+m.ph)*1.4, m.y, a);
      if (m.l<.35) g.star(Math.round(m.x)+4, Math.round(m.y)-4, m.l<.12?1:2, C.y);
    }
  };
  },

  "assembly"(E, R) {  // 11 · Self-assembling molecule
  const hex=[]; for (let i=0;i<6;i++){ const a=i*Math.PI/3; hex.push([Math.cos(a)*6,Math.sin(a)*6,'g']); }
  const TPL=[
    {at:[[0,0,'r'],[5,-3,'w'],[-5,-3,'w']], bd:[[0,1],[0,2]]},
    {at:hex, bd:[0,1,2,3,4,5].map(i=>[i,(i+1)%6,i%2===0])},
    {at:[[0,0,'g'],[6,0,'r'],[-6,0,'r']], bd:[[0,1,true],[0,2,true]]},
    {at:[[0,0,'g'],[5,0,'w'],[-5,0,'w'],[0,4,'w'],[0,-4,'w']], bd:[[0,1],[0,2],[0,3],[0,4]]},
  ];
  const L=[]; let acc=.8;
  return (t, dt, g) => {
    acc+=dt*.45;
    while (acc>1){ acc--; const tp=TPL[R()*TPL.length|0];
      L.push({tp, cx:E.ex-16, cy:E.y0+(E.y1-E.y0)*(.2+R()*.5), vx:-(4+R()*4)*E.k, vy:-(4+R()*3)*E.k, th:R()*6, w:(R()<.5?-1:1)*.35, l:0, max:8, spark:-1,
        sc:tp.at.map(()=>({x:E.ex-1, y:E.y0+R()*(E.y1-E.y0), vx:-(6+R()*14)*E.k, vy:-(2+R()*10)*E.k, wob:R()*6}))}); }
    for (let i=L.length-1;i>=0;i--){
      const m=L[i]; m.l+=dt; m.cx+=m.vx*dt; m.cy+=m.vy*dt; m.th+=m.w*dt;
      if (m.l>m.max||m.cy<E.top+7){L.splice(i,1);continue;}
      const k=smooth((m.l-.4)/2.2), a=life(m.l,m.max,.3,1.2), c=Math.cos(m.th), s=Math.sin(m.th);
      const pos=m.tp.at.map(([x,y],j)=>{ const sc=m.sc[j], tl=Math.min(m.l,1.6);
        const sx=sc.x+sc.vx*tl+Math.sin(m.l*3+sc.wob), sy=sc.y+sc.vy*tl+Math.cos(m.l*2.5+sc.wob);
        const tx=m.cx+x*c-y*s, ty=m.cy+x*s+y*c; return [sx+(tx-sx)*k, sy+(ty-sy)*k]; });
      if (k>.85){ const f=Math.min(1,(k-.85)/.15);
        m.tp.bd.forEach(([i0,i1,dbl])=>{ const A=pos[i0],B=pos[i1]; g.line(A[0],A[1],A[0]+(B[0]-A[0])*f,A[1]+(B[1]-A[1])*f,C.b,a);
          if (dbl&&f===1){ const mx=(A[0]+B[0])/2, my=(A[1]+B[1])/2, ox=(m.cx-mx)*.25, oy=(m.cy-my)*.25; g.line(A[0]*.7+B[0]*.3+ox,A[1]*.7+B[1]*.3+oy,A[0]*.3+B[0]*.7+ox,A[1]*.3+B[1]*.7+oy,C.d,a); } }); }
      pos.forEach(([x,y],j)=>atom2(g,x,y,m.tp.at[j][2],a));
      if (k>=1&&m.spark<0) m.spark=m.l;
      if (m.spark>=0&&m.l-m.spark<.5){ const p=(m.l-m.spark)/.5; g.star(Math.round(m.cx),Math.round(m.cy)-9,[1,2,2,1][p*4|0],C.y); }
    }
  };
  },

  "covalent"(E, R) {  // 12 · Covalent bond
  const L=[]; let acc=.7;
  return (t, dt, g) => {
    acc+=dt*.32;
    while (acc>1&&L.length<3){ acc--; L.push({x:E.ex-16, y:E.y0+(E.y1-E.y0)*(.25+R()*.5), vx:-(4+R()*4)*E.k, vy:-(3+R()*3)*E.k, ang:(R()-.5)*.9, l:0, max:8.5, ph:R()*6, spark:-1}); }
    if (acc>1) acc=1;
    for (let i=L.length-1;i>=0;i--){
      const m=L[i]; m.l+=dt; m.vx*=Math.pow(.75,dt); m.x+=m.vx*dt; m.y+=m.vy*dt;
      if (m.l>m.max||m.y<E.top+7){L.splice(i,1);continue;}
      const a=life(m.l,m.max,.6,1.2), d=24-14*smooth((m.l-.8)/2.4), bond=smooth((m.l-2.7)/.8);
      const ca=Math.cos(m.ang), sa=Math.sin(m.ang);
      const nuc=[[m.x-ca*d/2,m.y-sa*d/2],[m.x+ca*d/2,m.y+sa*d/2]];
      if (bond<1) nuc.forEach(([nx,ny],j)=>{
        for (let q=0;q<32;q+=2) g.px(nx+Math.cos(q/32*6.283)*5,ny+Math.sin(q/32*6.283)*4,C.n,a*(1-bond));
        const u=t*3+j*Math.PI+m.ph; g.px(nx+Math.cos(u)*5,ny+Math.sin(u)*4,C.w,a*(1-bond)); });
      if (bond>0){
        const A=d/2+5, path=u=>{ const lx=A*Math.cos(u), ly=10*Math.sin(u)*Math.cos(u); return [m.x+lx*ca-ly*sa, m.y+lx*sa+ly*ca]; };
        for (let q=0;q<48;q+=2){ const [x,y]=path(q/48*6.283); g.px(x,y,C.m,a*bond*.8); }
        for (const o of [0,Math.PI]){ const u=t*2.4+m.ph+o, [x,y]=path(u), [tx,ty]=path(u-.2); g.px(tx,ty,C.l,a*bond); g.px(x,y,C.w,a*bond); }
        if (m.spark<0&&bond>.5) m.spark=m.l;
      }
      g.sprite(["pbp","bpb","pbp"],nuc[0][0],nuc[0][1],a);
      g.sprite(["bpb","pbp","bpb"],nuc[1][0],nuc[1][1],a);
      if (m.spark>=0&&m.l-m.spark<.45){ const p=(m.l-m.spark)/.45; g.star(Math.round(m.x),Math.round(m.y)-8,[1,2,2,1][p*4|0],C.y); }
    }
  };
  },

  "satellites"(E, R) {  // 13 · Cell with satellites
  const L=[]; let acc=.7;
  return (t, dt, g) => {
    acc+=dt*.38;
    while (acc>1&&L.length<4){ acc--; const n=2+(R()*2|0);
      L.push({x:E.ex-9, y:E.y0+(E.y1-E.y0)*(.3+R()*.4), vx:-(5+R()*5)*E.k, vy:-(3+R()*3)*E.k, r:5, l:0, max:8.5,
        sats:Array.from({length:n},(_,j)=>({tilt:(R()-.5)*1.2+j*1.1, ph:R()*6, sp:1.4+R()*.8}))}); }
    if (acc>1) acc=1;
    for (let i=L.length-1;i>=0;i--){
      const c=L[i]; c.l+=dt; c.vx*=Math.pow(.7,dt); c.x+=c.vx*dt; c.y+=c.vy*dt;
      if (c.l>c.max||c.y<E.top+12){L.splice(i,1);continue;}
      const a=life(c.l,c.max,.6,1.2), X=Math.round(c.x), Y=Math.round(c.y);
      const sats=c.sats.map(s=>{ const u=t*s.sp+s.ph; return {s,u,p:ellipsePt(X,Y,12,4,s.tilt,u),front:Math.sin(u)>0}; });
      c.sats.forEach(s=>{ for (let q=0;q<45;q+=3){ const [x,y]=ellipsePt(X,Y,12,4,s.tilt,q/45*6.283); g.px(x,y,C.d,a); } });
      sats.filter(o=>!o.front).forEach(o=>water(g,o.p[0],o.p[1],o.u,a*.7,true));
      cellBlob(g,[{x:X,y:Y,r:c.r}],a);
      sats.filter(o=>o.front).forEach(o=>water(g,o.p[0],o.p[1],o.u,a,false));
    }
  };
  },

  "phage"(E, R) {  // 14 · Bacteriophage
  const L=[]; let acc=.6;
  return (t, dt, g) => {
    acc+=dt*.5;
    while (acc>1){ acc--; L.push({x:E.ex-6, y:E.y0+(E.y1-E.y0)*(.3+R()*.5), vx:-(5+R()*7)*E.k, vy:-(5+R()*4)*E.k, l:0, max:7.5, ph:R()*6}); }
    for (let i=L.length-1;i>=0;i--){
      const m=L[i]; m.l+=dt; m.vx*=Math.pow(.7,dt); m.x+=m.vx*dt; m.y+=m.vy*dt;
      if (m.l>m.max||m.y<E.top+10){L.splice(i,1);continue;}
      const a=life(m.l,m.max,.5,1.2), rot=-.35+.25*Math.sin(m.l*1.4+m.ph), c=Math.cos(rot), s=Math.sin(rot);
      const W=(lx,ly)=>[m.x+lx*c-ly*s, m.y+lx*s+ly*c];
      const hv=[]; for (let k=0;k<6;k++){ const an=k*Math.PI/3+Math.PI/6; hv.push(W(Math.cos(an)*4.6,-6+Math.sin(an)*4.6)); }
      const inHead=(x,y)=>{ let ins=false; for (let k=0,j=5;k<6;j=k++){ const [xi,yi]=hv[k],[xj,yj]=hv[j]; if ((yi>y)!==(yj>y)&&x<(xj-xi)*(y-yi)/(yj-yi)+xi) ins=!ins; } return ins; };
      const [hx,hy]=W(0,-6);
      for (let y=Math.floor(hy-6);y<=hy+6;y++) for (let x=Math.floor(hx-6);x<=hx+6;x++){
        if (!inHead(x+.5,y+.5)) continue;
        const edge=!inHead(x+1.5,y+.5)||!inHead(x-.5,y+.5)||!inHead(x+.5,y+1.5)||!inHead(x+.5,y-.5);
        g.px(x,y,edge?C.l:((x+y)&1?C.v:C.n),a);
      }
      g.px(hv[4][0],hv[4][1]+1,C.w,a);
      const seg=(p,q,col)=>{ const A=W(...p),B=W(...q); g.line(A[0],A[1],B[0],B[1],col,a); };
      seg([-2,-1],[2,-1],C.g); seg([0,-1],[0,5],C.g);
      for (const y of [1,3]){ const [x,yy]=W(0,y); g.px(x,yy,C.l,a); }
      seg([-3,5],[3,5],C.g);
      const wig=Math.sin(m.l*7+m.ph);
      for (const sd of [-1,1]) for (const sp of [1,2]){ seg([sd*sp,5],[sd*(sp+2),7+wig*sd*.6],C.l); seg([sd*(sp+2),7+wig*sd*.6],[sd*(sp+2.5),10],C.b); }
    }
  };
  },

  "protein"(E, R) {  // 15 · Protein folding
  const L=[]; let acc=.75; const N=16;
  return (t, dt, g) => {
    acc+=dt*.32;
    while (acc>1&&L.length<3){ acc--; L.push({sx:E.ex-1, sy:E.y0+(E.y1-E.y0)*(.25+R()*.5), vx:-(4+R()*4)*E.k, vy:-(4+R()*3)*E.k, l:0, max:9, rot:R()*6,
      cols:Array.from({length:N},()=>['o','m','p','b','y','m'][R()*6|0])}); }
    if (acc>1) acc=1;
    for (let i=L.length-1;i>=0;i--){
      const m=L[i]; m.l+=dt;
      if (m.l>m.max){L.splice(i,1);continue;}
      const a=life(m.l,m.max,.3,1.2), f=smooth((m.l-1.6)/2.6), mv=Math.max(0,m.l-1.2);
      const ax=m.sx+m.vx*mv, ay=m.sy+m.vy*mv;
      if (ay<E.top+8){L.splice(i,1);continue;}
      const pts=[];
      for (let j=0;j<N;j++){
        if (m.l<j*.07) break;
        const ex=-j*2.4-2, ey=Math.sin(j*.9+m.l*3)*1.6;
        const r=1.55*Math.sqrt(j+1), th=j*2.39996+m.rot+m.l*.3;
        const fx=-N*1.2+r*Math.cos(th), fy=r*Math.sin(th);
        const jit=Math.sin(m.l*9+j)*.7*f*(1-f)*4;
        pts.push([ax+ex+(fx-ex)*f+jit, ay+ey+(fy-ey)*f-jit, m.cols[j]]);
      }
      for (let j=1;j<pts.length;j++) g.line(pts[j-1][0],pts[j-1][1],pts[j][0],pts[j][1],C.n,a);
      pts.forEach(([x,y,c])=>atom2(g,x,y,c,a));
    }
  };
  },

  "vesicles"(E, R) {  // 16 · Vesicle release
  const V=[], F=[], M=[]; let acc=.6;
  return (t, dt, g) => {
    acc+=dt*.5;
    while (acc>1){ acc--; V.push({x:E.ex-8, y:E.y0+(E.y1-E.y0)*(.3+R()*.5), vx:-(6+R()*6)*E.k, vy:-(5+R()*4)*E.k, l:0, burst:2.6+R()*1.4, r:6,
      cargo:Array.from({length:3},()=>({dx:(R()-.5)*5, dy:(R()-.5)*5, c:['m','p','y','o'][R()*4|0], ph:R()*6}))}); }
    for (let i=V.length-1;i>=0;i--){
      const v=V[i]; v.l+=dt; v.vx*=Math.pow(.7,dt); v.x+=v.vx*dt; v.y+=v.vy*dt;
      const a=Math.min(1,v.l/.5);
      if (v.l>v.burst||v.y<E.top+8){ V.splice(i,1);
        for (let j=0;j<28;j+=2){ const an=j/28*6.283; F.push({x:v.x+Math.cos(an)*v.r,y:v.y+Math.sin(an)*v.r,vx:Math.cos(an)*9*E.k,vy:Math.sin(an)*9*E.k,l:0,c:j%4?C.l:C.b}); }
        v.cargo.forEach(c=>M.push({x:v.x+c.dx,y:v.y+c.dy,vx:c.dx*2*E.k-3*E.k,vy:c.dy*2*E.k-5*E.k,l:0,max:2.6,c:c.c,ph:c.ph})); continue; }
      for (let j=0;j<28;j++){ const an=j/28*6.283+v.l*.3; if (j%2===0) g.px(v.x+Math.cos(an)*v.r,v.y+Math.sin(an)*v.r,C.l,a); else g.px(v.x+Math.cos(an)*(v.r-2),v.y+Math.sin(an)*(v.r-2),C.b,a); }
      v.cargo.forEach(c=>{ const x=v.x+c.dx*.6+Math.sin(v.l*4+c.ph), y=v.y+c.dy*.6+Math.cos(v.l*3+c.ph); atom2(g,x,y,c.c,a); g.px(x+2,y-1,C.w,a); });
    }
    for (let i=F.length-1;i>=0;i--){ const f=F[i]; f.l+=dt; if (f.l>.6){F.splice(i,1);continue;} f.x+=f.vx*dt; f.y+=f.vy*dt; g.px(f.x,f.y,f.c,1-f.l/.6); }
    for (let i=M.length-1;i>=0;i--){ const c=M[i]; c.l+=dt; if (c.l>c.max||c.y<E.top+3){M.splice(i,1);continue;}
      c.vx*=Math.pow(.4,dt); c.vy=c.vy*Math.pow(.4,dt)-3*E.k*dt; c.x+=c.vx*dt; c.y+=c.vy*dt;
      const a=life(c.l,c.max,.01,1); atom2(g,c.x,c.y,c.c,a); g.px(c.x+2+Math.sin(c.l*3+c.ph),c.y-1,C.w,a); }
  };
  },

  "honeycomb"(E, R) {  // 17 · Honeycomb growth
  let seedFor=-1, cells=[]; const s=4.2;
  function build(seed){
    const r=rng(seed*104729+7); cells=[];
    const O=[E.ex-4, E.y0+(E.y1-E.y0)*.55], seen=new Set(['0,0']), q=[[0,0,0]];
    const ctr=(Q,Rr)=>[O[0]+s*Math.sqrt(3)*(Q+Rr/2), O[1]+s*1.5*Rr];
    while (q.length){ const [Q,Rr,d]=q.shift(); const [cx,cy]=ctr(Q,Rr); cells.push({cx,cy,birth:d*.42+r()*.2});
      if (d>=7) continue;
      for (const [dq,dr] of [[1,0],[-1,0],[0,1],[0,-1],[1,-1],[-1,1]]){ const k=(Q+dq)+','+(Rr+dr); if (seen.has(k)) continue; seen.add(k);
        const [nx,ny]=ctr(Q+dq,Rr+dr); if (nx>O[0]+1||ny>O[1]+3||nx<4||ny<E.top+4) continue; if (r()<.74) q.push([Q+dq,Rr+dr,d+1]); } }
  }
  return (t, dt, g) => {
    const cyc=10, n=Math.floor(t/cyc), tc=t%cyc; if (n!==seedFor){ seedFor=n; build(n); }
    const fade=tc>8.4?1-(tc-8.4)/1.6:1;
    cells.forEach(c=>{ const age=tc-c.birth; if (age<0) return; const grow=Math.min(1,age/.3), fresh=age<.45;
      const v=[]; for (let k=0;k<6;k++){ const an=Math.PI/6+k*Math.PI/3; v.push([c.cx+s*Math.cos(an),c.cy+s*Math.sin(an)]); }
      const edges=Math.ceil(grow*6);
      for (let k=0;k<edges;k++){ const A=v[k],B=v[(k+1)%6]; g.line(A[0],A[1],B[0],B[1],fresh?C.l:C.n,fade); }
      if (grow===1) v.forEach(([x,y])=>g.px(x,y,fresh?C.w:C.b,fade));
      if (age>.3&&age<.6) g.star(Math.round(c.cx),Math.round(c.cy),age<.45?1:0,C.y,fade); });
  };
  },

  "bacteria"(E, R) {  // 18 · Swimming bacteria
  const L=[]; let acc=.6;
  return (t, dt, g) => {
    acc+=dt*.7;
    while (acc>1){ acc--; L.push({x:E.ex-5, y:E.y0+(E.y1-E.y0)*(.2+R()*.6), l:0, max:6+R()*2, ph:R()*6, sp:(9+R()*5)*E.k, base:Math.PI+.7+R()*.5}); }
    for (let i=L.length-1;i>=0;i--){
      const b=L[i]; b.l+=dt;
      const hd=b.base+.45*Math.sin(b.l*1.3+b.ph), dx=Math.cos(hd), dy=Math.sin(hd);
      b.x+=dx*b.sp*dt; b.y+=dy*b.sp*dt;
      if (b.l>b.max||b.y<E.top+5||b.x<3){L.splice(i,1);continue;}
      const a=life(b.l,b.max,.4,1), H=[b.x+dx*3,b.y+dy*3], T=[b.x-dx*3,b.y-dy*3];
      const dist=(x,y)=>{ const vx=H[0]-T[0], vy=H[1]-T[1], u=Math.max(0,Math.min(1,((x-T[0])*vx+(y-T[1])*vy)/(vx*vx+vy*vy))); return Math.hypot(x-T[0]-vx*u,y-T[1]-vy*u); };
      for (let y=Math.floor(b.y-6);y<=b.y+6;y++) for (let x=Math.floor(b.x-6);x<=b.x+6;x++){
        if (dist(x+.5,y+.5)>2.1) continue;
        const edge=dist(x+1.5,y+.5)>2.1||dist(x-.5,y+.5)>2.1||dist(x+.5,y+1.5)>2.1||dist(x+.5,y-.5)>2.1;
        if (edge) g.px(x,y,C.m,a); else if ((x+y)&1) g.px(x,y,C.t,a); }
      g.px(b.x,b.y,C.y,a); g.px(b.x+dx*1.5,b.y+dy*1.5,C.y,a);
      for (let s=1;s<=9;s++){ const off=Math.sin(s*.9-b.l*12)*1.3*(s/9); g.px(T[0]-dx*s-dy*off, T[1]-dy*s+dx*off, s<5?C.l:C.b, a*(1-s/12)); }
    }
  };
  },

  "elements"(E, R) {  // 19 · Element tiles
  const FONT={H:["#.#","#.#","###","#.#","#.#"],C:["###","#..","#..","#..","###"],N:["#..#","##.#","#.##","#..#","#..#"],
              O:["###","#.#","#.#","#.#","###"],P:["##.","#.#","##.","#..","#.."],S:[".##","#..",".#.","..#","##."]};
  const COL={H:'l',C:'g',N:'b',O:'r',P:'o',S:'y'}, KEYS=Object.keys(FONT);
  function face(el){ const f=FONT[el], rows=[]; for (let y=0;y<9;y++){ let row=''; for (let x=0;x<9;x++){
      const edge=x===0||y===0||x===8||y===8; let ch=edge?'B':'k';
      const lx=x-Math.floor((9-f[0].length)/2), ly=y-2; if (!edge&&ly>=0&&ly<5&&lx>=0&&lx<f[0].length&&f[ly][lx]==='#') ch='w';
      row+=ch; } rows.push(row); } return rows; }
  const L=[]; let acc=.6;
  return (t, dt, g) => {
    acc+=dt*.75;
    while (acc>1){ acc--; const el=KEYS[R()*KEYS.length|0]; L.push({el, rows:face(el), x:E.ex-6, y:E.y0+(E.y1-E.y0)*(.2+R()*.6), vx:-(8+R()*10)*E.k, vy:-(5+R()*5)*E.k, l:0, max:6+R()*2, ph:R()*6, fl:1+R()*.8}); }
    for (let i=L.length-1;i>=0;i--){
      const m=L[i]; m.l+=dt; m.vx*=Math.pow(.5,dt); m.x+=m.vx*dt; m.y+=m.vy*dt;
      if (m.l>m.max||m.y<E.top+6){L.splice(i,1);continue;}
      const a=life(m.l,m.max,.4,1.2), cw=Math.cos(m.l*m.fl+m.ph), wd=Math.max(1,Math.round(9*Math.abs(cw)));
      const rows=m.rows.map(row=>{ let out=''; for (let x=0;x<wd;x++){ const src=row[Math.min(8,Math.floor((x+.5)/wd*9))]; out+=cw>=0?src:(src==='k'||src==='w'?'D':'B'); } return out; });
      g.spriteU(rows,m.x,m.y,a,{B:C[COL[m.el]],k:C.k,w:C.w,D:C.d});
    }
  };
  },

  "living-cell"(E, R) {  // 20 · Living cell
  const L=[]; let acc=.85;
  return (t, dt, g) => {
    acc+=dt*.22;
    while (acc>1&&L.length<2){ acc--; L.push({x:E.ex-13, y:E.y0+(E.y1-E.y0)*(.35+R()*.3), vx:-(4+R()*3)*E.k, vy:-(3+R()*2)*E.k, l:0, max:10, ph:R()*6}); }
    if (acc>1) acc=1;
    for (let i=L.length-1;i>=0;i--){
      const c=L[i]; c.l+=dt; c.vx*=Math.pow(.8,dt); c.x+=c.vx*dt; c.y+=c.vy*dt;
      if (c.l>c.max||c.y<E.top+12){L.splice(i,1);continue;}
      const a=life(c.l,c.max,.8,1.4), X=Math.round(c.x), Y=Math.round(c.y);
      const rad=(x,y)=>10+.8*Math.sin(Math.atan2(y-Y,x-X)*5+c.l*2+c.ph);
      const inside=(x,y)=>Math.hypot(x-X,y-Y)<=rad(x,y);
      for (let y=Y-12;y<=Y+12;y++) for (let x=X-12;x<=X+12;x++){
        if (!inside(x,y)) continue;
        if (!inside(x+1,y)||!inside(x-1,y)||!inside(x,y+1)||!inside(x,y-1)) g.px(x,y,C.m,a);
        else if ((x+2*y)%3===0) g.px(x,y,C.t,a); }
      const NX=X+1, NY=Y;
      for (let y=NY-4;y<=NY+4;y++) for (let x=NX-4;x<=NX+4;x++){ const d=Math.hypot(x-NX,y-NY); if (d>3.6) continue; g.px(x,y,d>2.6?C.v:((x+y)&1?C.n:C.d),a); }
      g.px(NX,NY,C.p,a); g.px(NX+1,NY,C.p,a);
      for (let k=0;k<4;k++){ const tilt=k*Math.PI/4+c.l*.2, u=t*2.2+k*1.7+c.ph;
        for (let q=0;q<36;q+=3){ const [x,y]=ellipsePt(NX,NY,7,2.6,tilt,q/36*6.283); g.px(x,y,C.d,a); }
        const [x,y]=ellipsePt(NX,NY,7,2.6,tilt,u); g.px(x,y,C.y,a); }
      const mx=X-5+Math.sin(c.l*.9)*1.5, my=Y+5+Math.cos(c.l*.7);
      g.line(mx-2,my,mx+2,my-1,C.o,a); g.px(mx,my,C.r,a);
    }
  };
  },
};

// Every effect, in board order. "from" names the round-1 ideas a round-2 one mixes.
export const CATALOG = [
  { n: 1, id: "bubbles", name: "Rising bubbles", from: null, desc: "Lab-glass bubbles drift out from the title and wobble upward, popping into four-pixel sparks near the top." },
  { n: 2, id: "dna", name: "DNA helix", from: null, desc: "A double helix uncoils from the edge of the title and curls up into the space, base pairs flickering as it turns." },
  { n: 3, id: "sparkles", name: "Eureka sparkles", from: null, desc: "The four-point stars from the flask moment in the animation, twinkling as they drift up and away from the title." },
  { n: 4, id: "cells", name: "Dividing cells", from: null, desc: "Cells bud out of the title, split by mitosis on the way up, and the daughters float off." },
  { n: 5, id: "molecules", name: "Floating molecules", from: null, desc: "Benzene, water, carbon dioxide and methane, turning slowly as they rise out of the title." },
  { n: 6, id: "spores", name: "Bioluminescent spores", from: null, desc: "Glowing spores meander up from the title, pulsing and leaving short dithered trails." },
  { n: 7, id: "fireworks", name: "Pipette fireworks", from: null, desc: "Droplets shoot up out of the title and burst at the top of their arc into coloured sparks." },
  { n: 8, id: "neurons", name: "Neuron growth", from: null, desc: "Dendrites branch out of the title into the space, then signals pulse along them to the tips, and it regrows." },
  { n: 9, id: "atoms", name: "Orbiting atoms", from: null, desc: "Bohr atoms with three tilted orbits drift up out of the title, electrons circling as they go." },
  { n: 10, id: "icons", name: "Lab icon float", from: null, desc: "Tiny flasks, test tubes, capsules, petri dishes, DNA and sprouts float up like balloons, each with a spark as it appears." },
  { n: 11, id: "assembly", name: "Self-assembling molecule", from: "5 + 9", desc: "Loose atoms drift out of the title, find each other and snap their bonds together into a molecule, with a spark when it completes." },
  { n: 12, id: "covalent", name: "Covalent bond", from: "9 + 5", desc: "Two atoms drift in close, their separate orbits dissolve, and the shared electrons start tracing a figure-eight around both nuclei." },
  { n: 13, id: "satellites", name: "Cell with satellites", from: "4 + 9 + 5", desc: "Cells rise with water molecules orbiting them like electrons, passing behind and in front of the membrane." },
  { n: 14, id: "phage", name: "Bacteriophage", from: "4", desc: "Little phage viruses rise with a slow sway: faceted head, striped tail, legs paddling as they climb." },
  { n: 15, id: "protein", name: "Protein folding", from: "5 + 4", desc: "A bead-chain of amino acids is pulled out of the title, then folds itself into a compact globule and floats away." },
  { n: 16, id: "vesicles", name: "Vesicle release", from: "4 + 5", desc: "Lipid-bilayer vesicles carry molecules up out of the title, then burst open and let their cargo drift free." },
  { n: 17, id: "honeycomb", name: "Honeycomb growth", from: "5", desc: "A benzene-ring lattice grows out of the title edge cell by cell, sparking as each ring closes, then dissolves and regrows." },
  { n: 18, id: "bacteria", name: "Swimming bacteria", from: "4", desc: "Rod-shaped bacteria swim up out of the title, wagging their flagella and weaving as they go." },
  { n: 19, id: "elements", name: "Element tiles", from: "5 + 9", desc: "Periodic-table tiles for H, C, N, O, P and S flip end over end as they float up. Letters stay readable on both sides." },
  { n: 20, id: "living-cell", name: "Living cell", from: "4 + 9", desc: "A big wobbling cell rises slowly; ribosomes orbit its nucleus like electrons and a mitochondrion drifts inside." },
];

// --- mounting into a page ----------------------------------------------------

/**
 * mountHeroEffect(container, options) -> { setEffect(name), destroy() }
 *
 * container  the hero element; it must be position: relative (set if static)
 * options:
 *   effect      an id from CATALOG, e.g. "cells"         (default "cells")
 *   title       the title element or a selector           (required)
 *   avoid       element or selector to stay above, e.g. the subtitle
 *   pixelSize   CSS px per art pixel, an integer, or a function returning one,
 *               read again on every layout                (default 3)
 *   gap         CSS px kept clear beside the title        (default 12)
 *   top         CSS px from the container top to clip at  (default 0)
 *   speed       motion multiplier                         (default 1)
 *   minSpace    hide when less than this many CSS px is free beside the title (default 96)
 *   seed        number, for a reproducible sequence       (default 1)
 *   reducedMotion  "static" (one still frame) | "hide"   (default "static")
 */
// The box the title's words occupy, not the element's. A heading is a block as
// wide as its column with the text centred inside it, so its own box puts the
// edge of the effects far out in the empty space rather than beside the words.
function textBox(title) {
  const range = document.createRange();
  range.selectNodeContents(title);
  const box = range.getBoundingClientRect();
  return box.width > 0 ? box : title.getBoundingClientRect();
}

export function mountHeroEffect(container, options = {}) {
  if (options.effect && !EFFECTS[options.effect]) throw new Error("hero-effects: unknown effect " + options.effect);
  const o = Object.assign({ effect: "cells", pixelSize: 3, gap: 12, top: 0, speed: 1, minSpace: 96, seed: 1, reducedMotion: "static" }, options);
  const pick = (x) => (typeof x === "string" ? container.querySelector(x) || document.querySelector(x) : x);
  const title = pick(o.title), avoid = o.avoid ? pick(o.avoid) : null;
  if (!title) throw new Error("hero-effects: options.title not found");
  if (getComputedStyle(container).position === "static") container.style.position = "relative";

  const canvas = document.createElement("canvas");
  canvas.setAttribute("aria-hidden", "true");
  Object.assign(canvas.style, { position: "absolute", left: "0", top: "0", pointerEvents: "none", imageRendering: "pixelated", zIndex: "0" });
  container.prepend(canvas);
  const ctx = canvas.getContext("2d");
  const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  let effect = o.effect, E, img, buf, g, tick, t = 0, raf = 0, visible = true, last = 0;

  function layout() {
    const P = Math.max(1, Math.round(typeof o.pixelSize === "function" ? o.pixelSize() : o.pixelSize));
    const cr = container.getBoundingClientRect(), tr = textBox(title);
    const left = tr.left - cr.left, top = tr.top - cr.top, bottom = tr.bottom - cr.top, cx = left + tr.width / 2;
    const floor = avoid ? avoid.getBoundingClientRect().top - cr.top - 4 : bottom + 24;
    const W = Math.ceil(cr.width / P) + 1, H = Math.ceil(cr.height / P);
    canvas.width = W; canvas.height = H;
    canvas.style.width = W * P + "px"; canvas.style.height = H * P + "px";
    E = { W, H, P, mx: Math.round((2 * cx) / P) - 1, ex: Math.floor((left - o.gap) / P) - 1, top: Math.ceil(o.top / P), bot: Math.floor(floor / P), y0: top / P, y1: bottom / P, k: (3.36 / P) * o.speed };
    canvas.style.display = left - o.gap < o.minSpace || (reduce && o.reducedMotion === "hide") ? "none" : "block";
    img = ctx.createImageData(W, H); buf = new Uint32Array(img.data.buffer);
    g = createRenderer(buf, E);
    tick = EFFECTS[effect](E, rng(o.seed));
    t = 0;
    for (let i = 0; i < 40; i++) { t += 0.1; buf.fill(0); tick(t, 0.1, g); } // open mid-flow, not empty
    ctx.putImageData(img, 0, 0);
  }
  function frame(now) {
    raf = requestAnimationFrame(frame);
    const dt = Math.min(0.05, (now - (last || now)) / 1000); last = now;
    t += dt; buf.fill(0); tick(t, dt, g); ctx.putImageData(img, 0, 0);
  }
  // No frame is asked for while nothing would be drawn: under reduced motion
  // the still frame `layout` paints is the whole of it, and off screen the
  // loop would wake sixty times a second to do nothing.
  function play() {
    if (raf || reduce || !visible) return;
    last = 0; raf = requestAnimationFrame(frame);
  }
  function pause() { cancelAnimationFrame(raf); raf = 0; }

  layout();
  const ro = new ResizeObserver(() => layout()); ro.observe(container); ro.observe(title);
  const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; visible ? play() : pause(); }); io.observe(container);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(layout); // the title moves when webfonts land
  play();

  return {
    setEffect(name) { if (!EFFECTS[name]) throw new Error("hero-effects: unknown effect " + name); effect = name; layout(); },
    destroy() { pause(); ro.disconnect(); io.disconnect(); canvas.remove(); },
  };
}
